import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  MenuItem,
  Skeleton,
  TextField,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { getSlideMarkerQcSummary } from 'api/markerTypes';
import { getMultiplexPanels, getMultiplexPanelsQueryKey } from 'api/multiplexPanels';
import { NON_SPECIFIC_STUDY_VALUES } from 'components/SearchFilters/hooks/useMainFilters';
import { MultiplexPanelView } from 'interfaces/multiplexPanels';
import { MultiplexPanelCellRules } from 'interfaces/study';
import { includes } from 'lodash';
import React, { useMemo, useState } from 'react';
import { useCellTypeOptions } from 'utils/queryHooks/useCellTypeOptions';
import { useQCLabels } from 'utils/queryHooks/useQCMarkers';
import { useStainTypeIdToDisplayName } from 'utils/useStainTypeIdToDisplayName';
import useStudy from 'utils/useStudy';
import CellRulesDataGrid from '.';
import CellRuleRevisionEditor from './CellRuleRevisionEditor';
import { cellRuleRevisionLabel, panelCellRuleMarkerIds, revisionsForPanel } from './cellRuleRevisions';
import { cellRuleRevisionWarnings } from './cellRuleRevisionWarnings';
import { GLOBAL_PANEL_STUDY_ID } from './GlobalCellRules';
import { projectStudyPanelCellRules } from './projectStudyPanelCellRules';

const panelsInCreationOrder = (panels: MultiplexPanelView[] | undefined) =>
  [...(panels ?? [])].sort(
    (left, right) =>
      (left.createdAt ?? left.savedAt ?? '').localeCompare(right.createdAt ?? right.savedAt ?? '') ||
      (left.name ?? left.panelId).localeCompare(right.name ?? right.panelId)
  );

const savedAtLabel = (savedAt?: string) => {
  if (!savedAt) return 'Save time unavailable';
  const parsed = new Date(savedAt);
  if (Number.isNaN(parsed.getTime())) return savedAt;
  return parsed.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};
const revisionIdentity = (entry: { panelId?: string | null; revision?: number; savedAt?: string }) =>
  `${entry.panelId ?? ''}|${entry.revision ?? 1}|${entry.savedAt ?? ''}`;

const scopeKey = (panelId?: string | null) => panelId ?? '';

const StudyMultiplexCellRules: React.FC<{ studyId: string; editable?: boolean }> = ({ studyId, editable = false }) => {
  const isSpecificStudy =
    Boolean(studyId) && studyId !== GLOBAL_PANEL_STUDY_ID && !includes(NON_SPECIFIC_STUDY_VALUES, studyId);
  const { study, isLoading, isError } = useStudy(studyId, { enabled: isSpecificStudy });
  const panelScope = useMemo(() => ({ studyId, filters: { studyId }, slidesMode: true as const }), [studyId]);
  const panelsQuery = useQuery({
    queryKey: getMultiplexPanelsQueryKey(panelScope),
    queryFn: ({ signal }) => getMultiplexPanels(panelScope, signal),
    enabled: isSpecificStudy,
  });
  const slideQcQuery = useQuery({
    queryKey: ['marker_qc_summary', panelScope],
    queryFn: ({ signal }) => getSlideMarkerQcSummary(panelScope, signal),
    enabled: isSpecificStudy,
  });
  const qcLabels = useQCLabels();
  const [selectedRevisionKey, setSelectedRevisionKey] = useState<Record<string, string>>({});
  const [appendedRevisions, setAppendedRevisions] = useState<MultiplexPanelCellRules[]>([]);
  const [draftingScopeKey, setDraftingScopeKey] = useState<string | null>(null);
  const { data: cellTypes } = useCellTypeOptions();
  const { stainTypeIdToDisplayName } = useStainTypeIdToDisplayName(isSpecificStudy ? studyId : undefined);
  const cellTypeName = (cellTypeId: string) =>
    cellTypes?.find((cellType) => cellType.id === cellTypeId)?.displayName || cellTypeId;
  const cellRuleEntries = useMemo(() => {
    const stored = study?.settings?.multiplexPanelCellRules ?? [];
    const known = new Set(stored.map(revisionIdentity));
    return [...stored, ...appendedRevisions.filter((entry) => !known.has(revisionIdentity(entry)))];
  }, [study?.settings?.multiplexPanelCellRules, appendedRevisions]);
  const revisionGroups = useMemo(() => {
    const panelIds = [
      ...new Set(
        cellRuleEntries.map((entry) => entry.panelId).filter((panelId): panelId is string => Boolean(panelId))
      ),
    ];
    return panelIds.map((panelId) => ({
      panelId,
      revisions: revisionsForPanel(cellRuleEntries, panelId),
    }));
  }, [cellRuleEntries]);
  const generalRevisions = useMemo(() => revisionsForPanel(cellRuleEntries, null), [cellRuleEntries]);
  const orderedPanels = useMemo(() => {
    const fromApi = panelsInCreationOrder(panelsQuery.data?.panels);
    const known = new Set(fromApi.map((panel) => panel.panelId));
    const fromSettings = [...(study?.settings?.multiplexPanels ?? [])]
      .filter((revision) => revision.panelId && !known.has(revision.panelId))
      .sort((left, right) => (left.savedAt ?? '').localeCompare(right.savedAt ?? ''))
      .filter(
        (revision, index, revisions) => revisions.findIndex((item) => item.panelId === revision.panelId) === index
      )
      .map((revision) => ({
        panelId: revision.panelId,
        name: revision.panelId,
        saved: true,
        channels: [],
        slides: [],
      }));
    return [...fromApi, ...fromSettings];
  }, [panelsQuery.data?.panels, study?.settings?.multiplexPanels]);
  const visiblePanels = useMemo(() => {
    const selectedFor = (panelId: string) => {
      const group = revisionGroups.find((item) => item.panelId === panelId);
      return (
        group?.revisions.find((entry) => `${entry.revision ?? 1}-${entry.savedAt}` === selectedRevisionKey[panelId]) ??
        group?.revisions[0]
      );
    };
    const project = (panelId: string) => {
      const selected = selectedFor(panelId);
      return selected
        ? projectStudyPanelCellRules([selected], panelsQuery.data?.panels, stainTypeIdToDisplayName, cellTypeName)
        : [];
    };
    if (!panelsQuery.data && orderedPanels.length === 0)
      return revisionGroups.flatMap((group) => project(group.panelId));
    const knownPanelIds = new Set(orderedPanels.map((panel) => panel.panelId));
    const definedPanels = orderedPanels.flatMap((panel) => {
      const projected = project(panel.panelId);
      return projected.length > 0
        ? projected
        : [
            {
              key: panel.panelId,
              panelId: panel.panelId,
              panelName: panel.name?.trim() || panel.panelId,
              columns: [],
              displayNames: {},
              cellTypeIds: [],
              cellTypeDisplayNames: {},
              captions: {},
              rules: [],
              hasRules: false,
            },
          ];
    });
    return [
      ...definedPanels,
      ...revisionGroups.flatMap((group) => (knownPanelIds.has(group.panelId) ? [] : project(group.panelId))),
    ];
  }, [orderedPanels, revisionGroups, selectedRevisionKey, panelsQuery.data, stainTypeIdToDisplayName, cellTypes]);
  const selectedGeneralRevision =
    generalRevisions.find((entry) => `${entry.revision ?? 1}-${entry.savedAt}` === selectedRevisionKey['']) ??
    generalRevisions[0];
  const generalView = selectedGeneralRevision
    ? projectStudyPanelCellRules([selectedGeneralRevision], [], stainTypeIdToDisplayName, cellTypeName)[0]
    : undefined;
  const rememberRevision = (revision: MultiplexPanelCellRules) => {
    setAppendedRevisions((current) => [...current, revision]);
    setSelectedRevisionKey((current) => ({
      ...current,
      [scopeKey(revision.panelId)]: `${revision.revision ?? 1}-${revision.savedAt}`,
    }));
  };

  if (!isSpecificStudy) return null;
  if (isLoading && !study) return <Skeleton variant="rounded" height={120} />;
  if (isError && !study) return <Typography color="error">Could not load saved cell rules.</Typography>;
  if (visiblePanels.length === 0 && generalRevisions.length === 0 && !editable) return null;

  return (
    <>
      <Typography variant="h3" sx={{ mb: 1 }}>
        Cell rules
      </Typography>
      {panelsQuery.isError ? (
        <Typography color="error" sx={{ mb: 1 }}>
          Panel names could not be loaded. Saved cell rules are still shown.
        </Typography>
      ) : null}
      {visiblePanels.map((panel) => (
        <Accordion key={panel.key} defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography>{panel.panelName}</Typography>
            {panel.savedAt ? (
              <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto', mr: 2 }}>
                Saved <time dateTime={panel.savedAt}>{savedAtLabel(panel.savedAt)}</time>
              </Typography>
            ) : null}
          </AccordionSummary>
          <AccordionDetails>
            {(revisionGroups.find((group) => group.panelId === panel.panelId)?.revisions.length ?? 0) > 0 && (
              <TextField
                select
                size="small"
                label="Cell rules revision"
                value={`${panel.revision ?? 1}-${panel.savedAt}`}
                disabled={draftingScopeKey === panel.panelId}
                onChange={(event) =>
                  setSelectedRevisionKey((current) => ({ ...current, [panel.panelId]: event.target.value }))
                }
                sx={{ mb: 1, minWidth: 280 }}
              >
                {(revisionGroups.find((group) => group.panelId === panel.panelId)?.revisions ?? []).map((revision) => (
                  <MenuItem
                    key={`${revision.revision ?? 1}-${revision.savedAt}`}
                    value={`${revision.revision ?? 1}-${revision.savedAt}`}
                  >
                    {`${cellRuleRevisionLabel(revision)} · ${savedAtLabel(revision.savedAt)}`}
                  </MenuItem>
                ))}
              </TextField>
            )}
            {cellRuleRevisionWarnings({
              revision:
                revisionGroups
                  .find((group) => group.panelId === panel.panelId)
                  ?.revisions.find(
                    (entry) => `${entry.revision ?? 1}-${entry.savedAt}` === selectedRevisionKey[panel.panelId]
                  ) ?? revisionGroups.find((group) => group.panelId === panel.panelId)?.revisions[0],
              displayNames: panel.displayNames,
              qcSettings: study?.settings?.qcMarkersSettings,
              slideSummaries: slideQcQuery.data,
              panelSlideIds: new Set(
                (panelsQuery.data?.panels ?? [])
                  .find((candidate) => candidate.panelId === panel.panelId)
                  ?.slides.map((slide) => slide.slideId) ?? []
              ),
              labelImpactById: Object.fromEntries((qcLabels.data ?? []).map((label) => [label.id, label.impactId])),
            }).map((warning) => (
              <Alert key={warning.id} severity="warning" sx={{ mb: 1 }}>
                {warning.message}
              </Alert>
            ))}
            {panel.hasRules && slideQcQuery.isLoading ? (
              <Alert severity="info" sx={{ mb: 1 }}>
                Marker QC status is still loading.
              </Alert>
            ) : null}
            {panel.hasRules && slideQcQuery.isError ? (
              <Alert severity="warning" sx={{ mb: 1 }}>
                Marker QC status could not be loaded.
              </Alert>
            ) : null}
            {panel.hasRules ? (
              <CellRulesDataGrid
                studyId={studyId}
                panelId={panel.panelId}
                stainTypeIds={panel.columns}
                markerDisplayNames={panel.displayNames}
                cellTypeIds={panel.cellTypeIds}
                cellTypeDisplayNames={panel.cellTypeDisplayNames}
                cellTypeCaptions={panel.captions}
                rules={panel.rules}
                isLoading={false}
                adminTableLayout
              />
            ) : (
              <Typography>No cell rules are saved for this panel.</Typography>
            )}
            <CellRuleRevisionEditor
              studyId={studyId}
              scopeId={panel.panelId}
              scopeLabel={panel.panelName}
              editable={editable}
              allowedMarkerIds={
                (panelsQuery.data?.panels ?? []).find((candidate) => candidate.panelId === panel.panelId)
                  ? panelCellRuleMarkerIds(
                      (panelsQuery.data?.panels ?? []).find((candidate) => candidate.panelId === panel.panelId)!
                    )
                  : panel.columns
              }
              markerDisplayNames={panel.displayNames}
              selectedRevision={
                revisionGroups
                  .find((group) => group.panelId === panel.panelId)
                  ?.revisions.find(
                    (entry) => `${entry.revision ?? 1}-${entry.savedAt}` === selectedRevisionKey[panel.panelId]
                  ) ?? revisionGroups.find((group) => group.panelId === panel.panelId)?.revisions[0]
              }
              onDraftingChange={(drafting) => setDraftingScopeKey(drafting ? panel.panelId : null)}
              onSaved={rememberRevision}
            />
          </AccordionDetails>
        </Accordion>
      ))}
      {editable || generalRevisions.length > 0 ? (
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography>General cell rules</Typography>
          </AccordionSummary>
          <AccordionDetails>
            {generalRevisions.length > 0 ? (
              <TextField
                select
                size="small"
                label="Cell rules revision"
                value={`${selectedGeneralRevision?.revision ?? 1}-${selectedGeneralRevision?.savedAt}`}
                disabled={draftingScopeKey === ''}
                onChange={(event) => setSelectedRevisionKey((current) => ({ ...current, '': event.target.value }))}
                sx={{ mb: 1, minWidth: 280 }}
              >
                {generalRevisions.map((revision) => (
                  <MenuItem
                    key={`${revision.revision ?? 1}-${revision.savedAt}`}
                    value={`${revision.revision ?? 1}-${revision.savedAt}`}
                  >
                    {`${cellRuleRevisionLabel(revision)} · ${savedAtLabel(revision.savedAt)}`}
                  </MenuItem>
                ))}
              </TextField>
            ) : (
              <Typography sx={{ mb: 1 }}>No general cell rules are saved.</Typography>
            )}
            {generalView?.hasRules ? (
              <CellRulesDataGrid
                studyId={studyId}
                panelId="general"
                stainTypeIds={generalView.columns}
                markerDisplayNames={generalView.displayNames}
                cellTypeIds={generalView.cellTypeIds}
                cellTypeDisplayNames={generalView.cellTypeDisplayNames}
                cellTypeCaptions={generalView.captions}
                rules={generalView.rules}
                isLoading={false}
                adminTableLayout
              />
            ) : null}
            <CellRuleRevisionEditor
              studyId={studyId}
              scopeId={null}
              scopeLabel="general cell rules"
              editable={editable}
              allowedMarkerIds={null}
              markerDisplayNames={generalView?.displayNames ?? {}}
              selectedRevision={selectedGeneralRevision}
              onDraftingChange={(drafting) => setDraftingScopeKey(drafting ? '' : null)}
              onSaved={rememberRevision}
            />
          </AccordionDetails>
        </Accordion>
      ) : null}
    </>
  );
};

export default StudyMultiplexCellRules;
